/**
 * Helper function to extract text content from React.ReactNode
 * Handles mixed content with text strings, numbers, and React elements
 */
export const extractTextFromChildren = (children: React.ReactNode): string => {
  if (children === null || children === undefined) {
    return "";
  }
  
  if (typeof children === "string" || typeof children === "number") {
    return String(children);
  }
  
  if (Array.isArray(children)) {
    return children.map(extractTextFromChildren).join("");
  }
  
  // Handle React elements - recursively extract text from their children
  if (typeof children === "object" && children !== null) {
    // Check if it's a React element (has $$typeof symbol or type property)
    const reactElement = children as React.ReactElement;
    if (reactElement.props?.children !== undefined) {
      return extractTextFromChildren(reactElement.props.children);
    }
  }
  
  return "";
};

/**
 * Helper function to detect if React.ReactNode contains Korean characters
 * Korean characters (Hangul) are in the Unicode ranges:
 * - Hangul Jamo: U+1100–U+11FF
 * - Hangul Compatibility Jamo: U+3130–U+318F
 * - Hangul Syllables: U+AC00–U+D7AF
 * - Hangul Jamo Extended-A: U+A960–U+A97F
 * - Hangul Jamo Extended-B: U+D7B0–U+D7FF
 */
export const containsKoreanCharacters = (children: React.ReactNode): boolean => {
  const text = extractTextFromChildren(children);
  
  if (!text) return false;
  
  // Regular expression to match Korean characters
  const koreanRegex = /[\u1100-\u11FF\u3130-\u318F\uAC00-\uD7AF\uA960-\uA97F\uD7B0-\uD7FF]/;
  return koreanRegex.test(text);
};
